import { useMutation, useQuery } from '@apollo/client'
import { useAuthContext } from 'shared/components'
import {
  GetCurrentUserDocument,
  GetIfUserExistsDocument,
  GetIfUserExistsQuery,
  GetIfUserExistsQueryVariables,
  GetLanguagesDocument,
  GetTimezonesDocument,
  SharedAccessZonesListDocument,
  SharedAccessZonesListQueryVariables,
} from './queries.generated'
import {
  UploadFileVariables,
  UploadFileResult,
  DeleteFilesVariables,
  UpdateResourceVariables,
} from './types'
import { DELETE_FILES, UPLOAD_FILE, UPDATE_RESOURCE } from './mutations'
import { GRAPHQL_ERROR_CODES } from 'shared/constants/graphql-errors'
import { ViewerMode, viewerModeVar } from 'shared/utils'
import { useSignOut } from 'shared/hooks/use-sign-out'

export const useCurrentUser = () => {
  const { user } = useAuthContext()
  const signOut = useSignOut()

  return useQuery(GetCurrentUserDocument, {
    fetchPolicy: 'cache-first',
    nextFetchPolicy: 'cache-only',
    skip: !user,
    onError: error => {
      const hasForbiddenError = error.graphQLErrors.some(
        error => error.extensions?.code === GRAPHQL_ERROR_CODES.FORBIDDEN
      )

      if (!hasForbiddenError) {
        return
      }

      if (viewerModeVar() === ViewerMode.SYSTEM_ADMIN) {
        return
      }

      void signOut()
    },
  })
}

export const useCurrentUserCacheOnly = () =>
  useQuery(GetCurrentUserDocument, {
    fetchPolicy: 'cache-only',
  })

export const useTimezones = (options?: { skip?: boolean }) =>
  useQuery(GetTimezonesDocument, {
    fetchPolicy: 'cache-first',
    ...(options ?? {}),
  })

export const useLanguages = (options?: { skip?: boolean }) =>
  useQuery(GetLanguagesDocument, {
    fetchPolicy: 'cache-first',
    ...(options ?? {}),
  })

export const useUploadFile = () =>
  useMutation<UploadFileResult, UploadFileVariables>(UPLOAD_FILE)

export const useDeleteFile = () =>
  useMutation<any, DeleteFilesVariables>(DELETE_FILES)

export const useUpdateResource = () =>
  useMutation<any, UpdateResourceVariables>(UPDATE_RESOURCE)

export const useUserByEmail = (
  variables: GetIfUserExistsQueryVariables,
  options?: { skip?: boolean }
) => {
  const query = useQuery<GetIfUserExistsQuery, GetIfUserExistsQueryVariables>(
    GetIfUserExistsDocument,
    {
      variables,
      ...(options ?? {}),
      fetchPolicy: 'cache-first',
    }
  )

  return {
    ...query,
    user: query.data?.viewer.user,
  }
}

export const useAccessZonesListQuery = (
  variables: SharedAccessZonesListQueryVariables
) =>
  useQuery(SharedAccessZonesListDocument, {
    variables,
    fetchPolicy: 'no-cache',
    notifyOnNetworkStatusChange: true,
  })
